<template>
  <div class="onboarding-view-root">
    <div v-if="!flagResolved" class="oe-state-message" data-testid="oe-loading">
      Loading onboarding…
    </div>

    <div
      v-else-if="!featureEnabled"
      class="oe-state-message oe-state-message--disabled"
      data-testid="oe-feature-disabled"
    >
      <v-icon aria-hidden="true">mdi-flag-outline</v-icon>
      <p class="oe-state-message__title">Onboarding editor is not enabled</p>
      <p class="oe-state-message__body">
        Turn on LaunchDarkly flag <strong>idea-MDP-600</strong> (Employee Onboarding Program) for
        your user or environment, then reload this page.
      </p>
    </div>

    <div v-else class="onboarding-editor" data-testid="onboarding-editor">
      <div v-if="!isSuperUser" class="oe-restricted" data-testid="oe-restricted">
        <v-icon aria-hidden="true">mdi-lock-outline</v-icon>
        <p>You do not have permission to author onboarding steps.</p>
      </div>

      <div v-else class="oe-layout">
        <!-- Module selector -->
        <div class="oe-modules" role="tablist" aria-label="Onboarding module">
          <button
            v-for="m in MODULES"
            :key="m.value"
            type="button"
            role="tab"
            :aria-selected="module === m.value ? 'true' : 'false'"
            class="oe-module-tab"
            :class="{ 'oe-module-tab--active': module === m.value }"
            :data-testid="`oe-module-${m.value}`"
            @click="onModuleChange(m.value)"
          >
            {{ m.label }}
          </button>
        </div>

        <div class="oe-body">
          <!-- Left: Steps panel -->
          <aside
            class="oe-steps-panel"
            :class="{ 'oe-steps-panel--collapsed': collapsed }"
            data-testid="oe-steps-panel"
          >
            <div class="oe-steps-header">
              <template v-if="!collapsed">
                <h2 class="oe-steps-title">Steps</h2>
                <span class="oe-steps-count">{{ steps.length }}</span>
              </template>
              <button
                type="button"
                class="oe-collapse-btn"
                :aria-label="collapsed ? 'Expand steps panel' : 'Collapse steps panel'"
                :aria-expanded="collapsed ? 'false' : 'true'"
                data-testid="oe-collapse-btn"
                @click="collapsed = !collapsed"
              >
                <v-icon aria-hidden="true">
                  {{ collapsed ? "mdi-chevron-right" : "mdi-chevron-left" }}
                </v-icon>
              </button>
            </div>

            <p
              v-if="loading && !steps.length"
              class="oe-steps-empty"
              data-testid="oe-steps-loading"
            >
              Loading steps…
            </p>
            <p
              v-else-if="errorMessage && !steps.length"
              class="oe-steps-empty oe-steps-error"
              data-testid="oe-steps-error"
            >
              {{ errorMessage }}
              <button type="button" class="oe-retry" @click="loadSteps">Retry</button>
            </p>
            <p v-else-if="!steps.length" class="oe-steps-empty" data-testid="oe-steps-empty">
              No steps yet.
            </p>

            <!-- Steps body: list/rail + Add step share one scroll container (ER-11844) -->
            <div class="oe-steps-body" data-testid="oe-steps-body">
              <!-- Expanded list -->
              <draggable
                v-if="!collapsed && steps.length"
                :list="steps"
                item-key="id"
                handle=".oe-drag-handle"
                class="oe-steps-list"
                ghost-class="oe-step-row--ghost"
                data-testid="oe-steps-list"
                @end="onReorder"
              >
                <div
                  v-for="step in steps"
                  :key="step.id"
                  class="oe-step-row"
                  :class="{ 'oe-step-row--selected': step.id === selectedId }"
                  :data-testid="`oe-step-row-${step.id}`"
                >
                  <span
                    class="oe-drag-handle"
                    role="img"
                    aria-label="Drag to reorder step"
                    :data-testid="`oe-drag-handle-${step.id}`"
                  >
                    <v-icon aria-hidden="true">mdi-drag</v-icon>
                  </span>
                  <span
                    class="oe-status-dot"
                    :class="`oe-status-dot--${step.status}`"
                    role="img"
                    :aria-label="STATUS_META[step.status]?.label || step.status"
                  ></span>
                  <button
                    type="button"
                    class="oe-step-select"
                    :data-testid="`oe-step-select-${step.id}`"
                    @click="selectStep(step.id)"
                  >
                    {{ stepLabel(step) }}
                  </button>
                </div>
              </draggable>

              <!-- Collapsed rail: one status dot per step -->
              <div
                v-else-if="collapsed && steps.length"
                class="oe-steps-rail"
                data-testid="oe-steps-rail"
              >
                <button
                  v-for="step in steps"
                  :key="step.id"
                  type="button"
                  class="oe-step-dot-btn"
                  :class="{ 'oe-step-dot-btn--selected': step.id === selectedId }"
                  :aria-label="`Select ${stepLabel(step)}`"
                  :title="stepLabel(step)"
                  :data-testid="`oe-step-dot-${step.id}`"
                  @click="selectStep(step.id)"
                >
                  <span
                    class="oe-status-dot"
                    :class="`oe-status-dot--${step.status}`"
                    aria-hidden="true"
                  ></span>
                </button>
              </div>

              <button
                v-if="!collapsed"
                type="button"
                class="oe-add-step"
                data-testid="oe-add-step"
                @click="addDialogOpen = true"
              >
                <v-icon aria-hidden="true">mdi-plus</v-icon>
                <span>Add step</span>
              </button>
              <button
                v-else
                type="button"
                class="oe-add-step oe-add-step--icon"
                aria-label="Add step"
                data-testid="oe-add-step-collapsed"
                @click="addDialogOpen = true"
              >
                <v-icon aria-hidden="true">mdi-plus</v-icon>
              </button>
            </div>
          </aside>

          <!-- Right: Step editor panel -->
          <section v-if="selectedStep" class="oe-editor-panel" data-testid="oe-editor-panel">
            <div class="oe-editor-header">
              <div class="oe-editor-meta">
                <span
                  class="oe-status-chip"
                  :class="`oe-status-chip--${selectedStep.status}`"
                  data-testid="oe-status-chip"
                >
                  {{ STATUS_META[selectedStep.status]?.label || selectedStep.status }}
                </span>
                <span class="oe-editor-label">{{ stepLabel(selectedStep) }}</span>
              </div>
              <span
                class="oe-editor-sequence"
                v-if="selectedStep.sequence != null"
                data-testid="oe-editor-sequence"
              >
                #{{ selectedStep.sequence }}
              </span>
            </div>

            <div class="oe-field">
              <label class="oe-field-label" for="oe-guide-key">Guide key</label>
              <input
                id="oe-guide-key"
                v-model="guideKeyDraft"
                type="text"
                class="oe-input"
                placeholder="Enter Knock guide key"
                data-testid="oe-guide-key"
                :disabled="saving"
              />
              <p class="oe-field-help">
                Referenced by key — guide content is authored and stored in Knock.
              </p>
            </div>

            <div class="oe-editor-body" data-testid="oe-editor-body">
              <TiptapEditor
                :value="editorDraftHtml"
                :editable="!saving"
                placeholder='Write "/" for commands…'
                @editor-change="onEditorChange"
                @image-upload-error="onImageUploadError"
              />
            </div>

            <div class="oe-editor-footer">
              <div class="oe-editor-actions">
                <button
                  v-if="transitionAction"
                  type="button"
                  class="oe-btn oe-btn--primary"
                  :disabled="transitioning || saving"
                  :data-testid="`oe-transition-${transitionAction.key}`"
                  @click="handleTransition"
                >
                  {{ transitionAction.label }}
                </button>
                <button
                  v-if="selectedStep.status === 'draft'"
                  type="button"
                  class="oe-btn oe-btn--danger"
                  :disabled="transitioning || saving"
                  data-testid="oe-delete"
                  @click="handleDelete"
                >
                  Delete
                </button>
              </div>
              <div class="oe-editor-save">
                <button
                  type="button"
                  class="oe-btn oe-btn--ghost"
                  :disabled="saving || !isDirty"
                  data-testid="oe-cancel"
                  @click="cancelEdit"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="oe-btn oe-btn--primary"
                  :disabled="saving || !isDirty"
                  data-testid="oe-save"
                  @click="saveStep"
                >
                  {{ saving ? "Saving…" : "Save" }}
                </button>
              </div>
            </div>
          </section>

          <section
            v-else
            class="oe-editor-panel oe-editor-panel--empty"
            data-testid="oe-editor-empty"
          >
            <p>Select a step to edit, or add a new step.</p>
            <button
              type="button"
              class="oe-btn oe-btn--primary"
              data-testid="oe-add-step-empty"
              @click="addDialogOpen = true"
            >
              Add step
            </button>
          </section>
        </div>
      </div>

      <AddOnboardingStepDialog
        :open="addDialogOpen"
        @save="handleAddStep"
        @close="addDialogOpen = false"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from "vue";
import draggable from "vuedraggable";

import TiptapEditor from "@components/TiptapEditor/TiptapEditor.vue";
import AddOnboardingStepDialog from "@components/AddOnboardingStepDialog/AddOnboardingStepDialog.vue";
import { LD_FLAG_IDEA_MDP_600 } from "@components/constants/launchDarklyFlagKeys";

import {
  getOnboardingSteps,
  createOnboardingStep,
  updateOnboardingStep,
  deleteOnboardingStep,
  activateOnboardingStep,
  retireOnboardingStep,
  reactivateOnboardingStep,
  reorderOnboardingSteps,
} from "@/api";
import { isAdminSysadmin } from "@/utils/isAdminSysadmin";

/**
 * ER-11474 — OnboardingEditor (admin.exiqtive.com step-authoring UI).
 *
 * Rule 8 of Employee Onboarding Program — Data Model, Lifecycle and Rules v2.
 * Left: a per-module steps list (drag-to-reorder, status dot, "+ Add step")
 * that can collapse to a narrow rail of status dots; right: the step editor
 * (guide_key, Tiptap rich text, Rule 3 status transitions, draft-only delete,
 * save/cancel footer). Authoring is restricted to platform super-users and the
 * whole surface ships behind the linked MDP-600 kill switch (idea-MDP-600).
 */

const MODULES = [
  { value: "YOUR_STUFF", label: "Your Stuff" },
  { value: "ORGANIZATION", label: "Organization" },
  { value: "FAIR_PAY", label: "Fair Pay" },
];

const STATUS_META = {
  draft: { label: "Draft", color: "#98A2B3" },
  active: { label: "Active", color: "#12B76A" },
  retired: { label: "Retired", color: "#F79009" },
};

const proxy = getCurrentInstance()?.proxy;

const flagResolved = ref(false);
const featureEnabled = ref(false);
const isSuperUser = ref(false);

const module = ref("YOUR_STUFF");
const steps = ref([]);
const loading = ref(false);
const errorMessage = ref("");
const collapsed = ref(false);

const selectedId = ref(null);
const guideKeyDraft = ref("");
const editorDraftHtml = ref("");
const editorDraftJson = ref(null);

const saving = ref(false);
const transitioning = ref(false);
const addDialogOpen = ref(false);

const selectedStep = computed(() => steps.value.find((s) => s.id === selectedId.value) || null);

const isDirty = computed(() => {
  const step = selectedStep.value;
  if (!step) return false;
  if ((guideKeyDraft.value || "") !== (step.guide_key || "")) return true;
  if ((editorDraftHtml.value || "") !== (step.text || "")) return true;
  return false;
});

const transitionAction = computed(() => {
  const step = selectedStep.value;
  if (!step) return null;
  if (step.status === "draft") return { key: "activate", label: "Activate" };
  if (step.status === "active") return { key: "retire", label: "Retire" };
  if (step.status === "retired") return { key: "reactivate", label: "Reactivate" };
  return null;
});

function readIsSuperUser() {
  return isAdminSysadmin();
}

function stripHtml(html) {
  return String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim();
}

function stepLabel(step) {
  return stripHtml(step.text) || "Untitled step";
}

function notifyError(context, error) {
  const detail = error?.response?.data?.detail || error?.message || "Something went wrong.";
  proxy?.$notifications?.error(`${context}: ${detail}`);
}

async function loadSteps() {
  loading.value = true;
  errorMessage.value = "";
  try {
    const response = await getOnboardingSteps({ module: module.value });
    const list = Array.isArray(response?.data) ? response.data : [];
    steps.value = [...list].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

    // Preserve the current selection when it still exists; otherwise pick the first.
    if (!steps.value.some((s) => s.id === selectedId.value)) {
      selectedId.value = steps.value.length ? steps.value[0].id : null;
    }
    hydrateEditorDrafts();
  } catch (error) {
    errorMessage.value = "Failed to load steps.";
    console.error("Failed to load onboarding steps:", error);
  } finally {
    loading.value = false;
  }
}

function hydrateEditorDrafts() {
  const step = selectedStep.value;
  guideKeyDraft.value = step ? step.guide_key || "" : "";
  editorDraftHtml.value = step ? step.text || "" : "";
  editorDraftJson.value = step
    ? step.tip_tap_content || { type: "doc", content: [] }
    : { type: "doc", content: [] };
}

function selectStep(id) {
  selectedId.value = id;
  hydrateEditorDrafts();
}

function onModuleChange(value) {
  if (value === module.value) return;
  module.value = value;
  selectedId.value = null;
  loadSteps();
}

function onEditorChange({ html, json }) {
  editorDraftHtml.value = html || "";
  editorDraftJson.value = json || { type: "doc", content: [] };
}

function onImageUploadError({ message } = {}) {
  proxy?.$notifications?.error(message || "Image upload failed.");
}

function cancelEdit() {
  hydrateEditorDrafts();
}

async function saveStep() {
  const step = selectedStep.value;
  if (!step || saving.value) return;
  saving.value = true;
  try {
    const response = await updateOnboardingStep(step.id, {
      guide_key: guideKeyDraft.value,
      tip_tap_content: editorDraftJson.value,
      text: editorDraftHtml.value,
    });
    replaceStep(response?.data || step);
    proxy?.$notifications?.success("Step saved.");
  } catch (error) {
    notifyError("Save failed", error);
  } finally {
    saving.value = false;
  }
}

async function handleAddStep(name) {
  addDialogOpen.value = false;
  try {
    const response = await createOnboardingStep({
      module: module.value,
      name,
    });
    await loadSteps();
    if (response?.data?.id) {
      selectedId.value = response.data.id;
      hydrateEditorDrafts();
    }
    proxy?.$notifications?.success("Step created as draft.");
  } catch (error) {
    notifyError("Add step failed", error);
  }
}

async function handleTransition() {
  const step = selectedStep.value;
  if (!step || transitioning.value) return;
  transitioning.value = true;
  try {
    const response =
      step.status === "draft"
        ? await activateOnboardingStep(step.id)
        : step.status === "active"
        ? await retireOnboardingStep(step.id)
        : await reactivateOnboardingStep(step.id);
    replaceStep(response?.data || { ...step, status: nextStatus(step.status) });
    proxy?.$notifications?.success("Step status updated.");
  } catch (error) {
    notifyError("Transition failed", error);
  } finally {
    transitioning.value = false;
  }
}

function nextStatus(status) {
  if (status === "draft") return "active";
  if (status === "active") return "retired";
  if (status === "retired") return "active";
  return status;
}

async function handleDelete() {
  const step = selectedStep.value;
  if (!step || step.status !== "draft") return;
  if (!window.confirm("Delete this draft step? This cannot be undone.")) return;
  try {
    await deleteOnboardingStep(step.id);
    proxy?.$notifications?.success("Step deleted.");
    await loadSteps();
  } catch (error) {
    notifyError("Delete failed", error);
  }
}

async function onReorder() {
  const orderedIds = steps.value.map((s) => s.id);
  try {
    const response = await reorderOnboardingSteps({
      module: module.value,
      ordered_step_ids: orderedIds,
    });
    if (Array.isArray(response?.data)) {
      steps.value = [...response.data].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
    }
  } catch (error) {
    notifyError("Reorder failed", error);
    await loadSteps();
  }
}

function replaceStep(updated) {
  const index = steps.value.findIndex((s) => s.id === updated.id);
  if (index !== -1) {
    steps.value.splice(index, 1, updated);
  }
  hydrateEditorDrafts();
}

onMounted(async () => {
  isSuperUser.value = readIsSuperUser();
  try {
    featureEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_600, true)) !== false;
  } catch {
    featureEnabled.value = true;
  } finally {
    flagResolved.value = true;
  }
  if (featureEnabled.value && isSuperUser.value) {
    await loadSteps();
  }
});
</script>

<style scoped>
.onboarding-view-root {
  min-height: 360px;
  width: 100%;
}

.oe-state-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 320px;
  padding: 24px;
  text-align: center;
  color: #475467;
  font-size: 14px;
  line-height: 20px;
}

.oe-state-message--disabled .oe-state-message__title {
  margin: 8px 0 0;
  font-size: 16px;
  font-weight: 600;
  color: #101828;
}

.oe-state-message__body {
  margin: 0;
  max-width: 420px;
}

.onboarding-editor {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f9fafb;
  font-family: Inter, sans-serif;
  color: #101828;
}

.oe-restricted {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 320px;
  color: #475467;
  font-size: 14px;
  line-height: 20px;
}

.oe-layout {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
}

.oe-modules {
  display: flex;
  gap: 4px;
  padding: 12px 16px;
  background: #ffffff;
  border-bottom: 1px solid #eaecf0;
}

.oe-module-tab {
  padding: 8px 14px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #ffffff;
  color: #344054;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  cursor: pointer;
}

.oe-module-tab--active {
  background: #7f56d9;
  border-color: #7f56d9;
  color: #ffffff;
}

.oe-module-tab:focus-visible,
.oe-btn:focus-visible,
.oe-step-select:focus-visible,
.oe-collapse-btn:focus-visible,
.oe-add-step:focus-visible,
.oe-step-dot-btn:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 1px;
}

.oe-body {
  display: flex;
  flex: 1;
  min-height: 0;
}

.oe-steps-panel {
  display: flex;
  flex-direction: column;
  width: 280px;
  min-width: 280px;
  background: #ffffff;
  border-right: 1px solid #eaecf0;
  transition: width 0.2s ease, min-width 0.2s ease;
}

.oe-steps-panel--collapsed {
  width: 56px;
  min-width: 56px;
}

.oe-steps-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid #eaecf0;
}

.oe-steps-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
}

.oe-steps-count {
  padding: 1px 8px;
  border-radius: 12px;
  background: #f2f4f7;
  color: #475467;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
}

.oe-collapse-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #ffffff;
  color: #475467;
  cursor: pointer;
}

.oe-steps-empty {
  margin: 0;
  padding: 16px;
  color: #667085;
  font-size: 14px;
  line-height: 20px;
}

.oe-steps-error {
  color: #b42318;
}

.oe-retry {
  margin-left: 8px;
  padding: 0;
  border: none;
  background: none;
  color: #7f56d9;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
}

.oe-steps-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.oe-steps-list {
  padding: 8px;
}

.oe-step-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 8px;
}

.oe-step-row:hover {
  background: #f9fafb;
}

.oe-step-row--selected {
  background: #f4ebff;
}

.oe-step-row--ghost {
  opacity: 0.4;
  background: #e4e7ec;
}

.oe-drag-handle {
  display: inline-flex;
  align-items: center;
  color: #98a2b3;
  cursor: grab;
}

.oe-status-dot {
  display: inline-block;
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.oe-status-dot--draft {
  background: #98a2b3;
}

.oe-status-dot--active {
  background: #12b76a;
}

.oe-status-dot--retired {
  background: #f79009;
}

.oe-step-select {
  flex: 1;
  min-width: 0;
  padding: 0;
  border: none;
  background: none;
  color: #101828;
  font-size: 14px;
  line-height: 20px;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}

.oe-steps-rail {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px 0;
}

.oe-step-dot-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 8px;
  background: none;
  cursor: pointer;
}

.oe-step-dot-btn--selected {
  border-color: #7f56d9;
  background: #f4ebff;
}

.oe-add-step {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 8px;
  padding: 8px;
  border: 1px dashed #d0d5dd;
  border-radius: 8px;
  background: #ffffff;
  color: #7f56d9;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
}

.oe-add-step--icon {
  width: 40px;
  height: 40px;
  margin: 8px auto;
}

.oe-editor-panel {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  padding: 20px 24px;
  background: #ffffff;
  overflow-y: auto;
}

.oe-editor-panel--empty {
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: #475467;
  font-size: 14px;
  line-height: 20px;
}

.oe-editor-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.oe-editor-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.oe-status-chip {
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  text-transform: capitalize;
}

.oe-status-chip--draft {
  background: #f2f4f7;
  color: #475467;
}

.oe-status-chip--active {
  background: #ecfdf3;
  color: #027a48;
}

.oe-status-chip--retired {
  background: #fffaeb;
  color: #b54708;
}

.oe-editor-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
}

.oe-editor-sequence {
  color: #667085;
  font-size: 13px;
  line-height: 20px;
}

.oe-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
  max-width: 560px;
}

.oe-field-label {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}

.oe-input {
  box-sizing: border-box;
  width: 100%;
  height: 44px;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font-family: inherit;
  font-size: 16px;
  line-height: 24px;
  color: #101828;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.oe-input:focus {
  border-color: #7f56d9;
  outline: 2px solid #7f56d9;
  outline-offset: 1px;
}

.oe-field-help {
  margin: 0;
  font-size: 12px;
  line-height: 18px;
  color: #667085;
}

.oe-editor-body {
  flex: 1;
  min-height: 260px;
  border: 1px solid #eaecf0;
  border-radius: 12px;
}

.oe-editor-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #eaecf0;
}

.oe-editor-actions,
.oe-editor-save {
  display: flex;
  align-items: center;
  gap: 8px;
}

.oe-btn {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  min-width: 64px;
  padding: 0 16px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
}

.oe-btn--primary {
  background: #7f56d9;
  border: 1px solid #7f56d9;
  color: #ffffff;
}

.oe-btn--danger {
  background: #ffffff;
  border: 1px solid #f04438;
  color: #f04438;
}

.oe-btn--ghost {
  background: #ffffff;
  border: 1px solid #d0d5dd;
  color: #344054;
}

.oe-btn:disabled {
  background: #f2f4f7;
  border-color: #eaecf0;
  color: #98a2b3;
  cursor: not-allowed;
}
</style>
